import React from 'react';
import { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { OPTIMIZATION_GOAL_LABELS } from '@frontend/applications/Social/constants/optimization-goals';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { BoostPostFormValues } from '../types/boost-post-schema';

interface ConversionTrackingFieldsProps {
  control: Control<BoostPostFormValues>;
  optimizationGoal?: string;
}

const ATTRIBUTION_EVENT_TYPES = [
  { value: 'CLICK', label: 'Click' },
  { value: 'VIEW', label: 'View' },
];

const ATTRIBUTION_WINDOW_DAYS = [
  { value: 1, label: '1 Day' },
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 28, label: '28 Days' },
];

const isConversionBasedGoal = (goal?: string): boolean => {
  // Meta API requires promoted_object for these optimization goals
  return (
    goal === 'OFFSITE_CONVERSIONS' || goal === 'LINK_CLICKS' || goal === 'APP_INSTALLS' || goal === 'LEAD_GENERATION'
  );
};

export const ConversionTrackingFields: React.FC<ConversionTrackingFieldsProps> = ({ control, optimizationGoal }) => {
  const showConversionTracking = isConversionBasedGoal(optimizationGoal);

  if (!showConversionTracking) {
    return null;
  }

  // Determine which fields to show based on optimization goal
  const showPixelId =
    optimizationGoal === 'OFFSITE_CONVERSIONS' ||
    optimizationGoal === 'LINK_CLICKS' ||
    optimizationGoal === 'LEAD_GENERATION';
  const showCustomEventType = optimizationGoal === 'OFFSITE_CONVERSIONS';
  const showAppId = optimizationGoal === 'APP_INSTALLS';
  const showCustomConversionId = optimizationGoal === 'OFFSITE_CONVERSIONS';

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">Conversion Tracking</CardTitle>
        <CardDescription className="text-xs text-muted-foreground">
          Required fields for {OPTIMIZATION_GOAL_LABELS.OFFSITE_CONVERSIONS}, {OPTIMIZATION_GOAL_LABELS.LINK_CLICKS},{' '}
          {OPTIMIZATION_GOAL_LABELS.APP_INSTALLS}, and {OPTIMIZATION_GOAL_LABELS.LEAD_GENERATION} optimization goals
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pixel ID */}
        {showPixelId && (
          <div className="space-y-2">
            <Label htmlFor="pixelId" className="text-sm font-medium">
              Pixel ID *
            </Label>
            <Controller
              name="adSet.promotedObject.pixelId"
              control={control}
              render={({ field, fieldState }) => (
                <div>
                  <Input
                    id="pixelId"
                    placeholder="Enter your Meta Pixel ID"
                    {...field}
                    className={fieldState.error ? 'border-red-500' : ''}
                  />
                  {fieldState.error && <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>}
                </div>
              )}
            />
          </div>
        )}

        {/* App ID */}
        {showAppId && (
          <div className="space-y-2">
            <Label htmlFor="appId" className="text-sm font-medium">
              App ID *
            </Label>
            <Controller
              name="adSet.promotedObject.appId"
              control={control}
              render={({ field, fieldState }) => (
                <div>
                  <Input
                    id="appId"
                    placeholder="Enter your App ID"
                    {...field}
                    className={fieldState.error ? 'border-red-500' : ''}
                  />
                  {fieldState.error && <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>}
                </div>
              )}
            />
          </div>
        )}

        {/* Custom Event Type */}
        {showCustomEventType && (
          <div className="space-y-2">
            <Label htmlFor="customEventType" className="text-sm font-medium">
              Custom Event Type
            </Label>
            <Controller
              name="adSet.promotedObject.customEventType"
              control={control}
              render={({ field }) => <Input id="customEventType" placeholder="e.g., PURCHASE" {...field} />}
            />
          </div>
        )}

        {/* Custom Conversion ID */}
        {showCustomConversionId && (
          <div className="space-y-2">
            <Label htmlFor="customConversionId" className="text-sm font-medium">
              Custom Conversion ID
            </Label>
            <Controller
              name="adSet.promotedObject.customConversionId"
              control={control}
              render={({ field }) => (
                <Input id="customConversionId" placeholder="Enter your Custom Conversion ID" {...field} />
              )}
            />
          </div>
        )}

        {/* Attribution Spec */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Attribution Settings (Optional)</Label>
          <p className="text-xs text-muted-foreground">Override default attribution settings if needed</p>
          <Controller
            name="adSet.attributionSpec"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={field.value?.[0]?.eventType || ''}
                    onValueChange={(value) => {
                      const currentSpec = field.value?.[0] || { eventType: '', windowDays: 0 };
                      field.onChange([{ ...currentSpec, eventType: value }]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Event Type" />
                    </SelectTrigger>
                    <SelectContent className="z-[1000]">
                      {ATTRIBUTION_EVENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={field.value?.[0]?.windowDays?.toString() || ''}
                    onValueChange={(value) => {
                      const currentSpec = field.value?.[0] || { eventType: '', windowDays: 0 };
                      field.onChange([{ ...currentSpec, windowDays: Number(value) }]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Window" />
                    </SelectTrigger>
                    <SelectContent className="z-[1000]">
                      {ATTRIBUTION_WINDOW_DAYS.map((window) => (
                        <SelectItem key={window.value} value={window.value.toString()}>
                          {window.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {fieldState.error && <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>}
              </div>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};
